import { setState, getState, GameState } from './store';

/** Strict type for player-driven advances. Per-location state is forbidden
 *  in extraPatches — the helper always resets it. */
export type AdvancePatches = Omit<
  Partial<GameState>,
  'currentLocationIndex' | 'geoVerified' | 'hintNavigateData'
>;

/** Looser type for server-authoritative reconciles. Caller MAY override
 *  per-location state by explicit set when serverIdx > current (server
 *  knows fresh values for the new location). When serverIdx <= current,
 *  per-location fields in extraPatches are IGNORED — server's view is
 *  stale on the index, can't be authoritative on per-location state for
 *  our current position. currentLocationIndex still forbidden — that's
 *  the first positional argument. */
export type ReconcilePatches = Omit<Partial<GameState>, 'currentLocationIndex'>;

export function advanceLocationIndex(toIdx: number, extraPatches: AdvancePatches = {}): void {
  setState({
    ...extraPatches,
    currentLocationIndex: toIdx,
    geoVerified: undefined,
    hintNavigateData: undefined,
  });
}

export function reconcileLocationIndex(serverIdx: number, extraPatches: ReconcilePatches = {}): void {
  const current = getState().currentLocationIndex;
  if (serverIdx > current) {
    // Server says we advanced. Default-reset per-location state, but let
    // caller override by explicitly setting geoVerified/hintNavigateData
    // (server may know fresh values for the new location).
    setState({
      geoVerified: undefined,
      hintNavigateData: undefined,
      ...extraPatches,
      currentLocationIndex: serverIdx,
    });
  } else {
    // serverIdx <= current — server is stale on the index. Server's per-
    // location state (if any) is for the stale index, not authoritative
    // for our current position. Strip per-location fields from extraPatches
    // so the local geoVerified/hintNavigateData survive.
    const { geoVerified: _geo, hintNavigateData: _hint, ...safePatches } = extraPatches;
    setState({ ...safePatches, currentLocationIndex: serverIdx });
  }
}
